import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Settings,
  User,
  Bell,
  Shield,
  Palette,
  Globe,
  Moon,
  Sun,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    campaignAlerts: true,
    screenAlerts: true,
    billingAlerts: false,
  });

  const handleSave = () => {
    toast.success('Settings saved successfully');
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'operator': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'client': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      default: return 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20';
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
          Settings
        </h1>
        <p className="text-zinc-400 mt-1">Manage your account preferences</p>
      </div>

      {/* Profile Section */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <User className="w-5 h-5 text-blue-500" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-6">
            <Avatar className="w-20 h-20">
              <AvatarFallback className="bg-zinc-800 text-zinc-300 text-2xl">
                {user?.name?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <h3 className="text-xl font-semibold text-white">{user?.name}</h3>
              <p className="text-zinc-400">{user?.email}</p>
              <Badge variant="outline" className={getRoleBadgeColor(user?.role)}>
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
              </Badge>
            </div>
          </div>

          <Separator className="bg-zinc-800" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-300">Full Name</Label>
              <Input
                defaultValue={user?.name}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                data-testid="settings-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Email</Label>
              <Input
                defaultValue={user?.email}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                disabled
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-500" data-testid="save-profile-btn">
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notifications Section */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Bell className="w-5 h-5 text-blue-500" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-white font-medium">Email Notifications</p>
              <p className="text-sm text-zinc-500">Receive updates via email</p>
            </div>
            <Switch
              checked={notifications.email}
              onCheckedChange={(checked) => setNotifications({ ...notifications, email: checked })}
              data-testid="email-notifications-switch"
            />
          </div>

          <Separator className="bg-zinc-800" />

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-white font-medium">Push Notifications</p>
              <p className="text-sm text-zinc-500">Receive push notifications in browser</p>
            </div>
            <Switch
              checked={notifications.push}
              onCheckedChange={(checked) => setNotifications({ ...notifications, push: checked })}
            />
          </div>

          <Separator className="bg-zinc-800" />

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-white font-medium">Campaign Alerts</p>
              <p className="text-sm text-zinc-500">Get notified about campaign status changes</p>
            </div>
            <Switch
              checked={notifications.campaignAlerts}
              onCheckedChange={(checked) => setNotifications({ ...notifications, campaignAlerts: checked })}
            />
          </div>

          <Separator className="bg-zinc-800" />

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-white font-medium">Screen Alerts</p>
              <p className="text-sm text-zinc-500">Get notified when screens go offline</p>
            </div>
            <Switch
              checked={notifications.screenAlerts}
              onCheckedChange={(checked) => setNotifications({ ...notifications, screenAlerts: checked })}
            />
          </div>

          <Separator className="bg-zinc-800" />

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-white font-medium">Billing Alerts</p>
              <p className="text-sm text-zinc-500">Get notified about invoices and payments</p>
            </div>
            <Switch
              checked={notifications.billingAlerts}
              onCheckedChange={(checked) => setNotifications({ ...notifications, billingAlerts: checked })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Appearance Section */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Palette className="w-5 h-5 text-blue-500" />
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-white font-medium">Dark Mode</p>
              <p className="text-sm text-zinc-500">Use dark theme (always enabled)</p>
            </div>
            <div className="flex items-center gap-2">
              <Moon className="w-5 h-5 text-blue-500" />
              <Switch checked={true} disabled />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Section */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Shield className="w-5 h-5 text-blue-500" />
            Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-zinc-300">Current Password</Label>
            <Input
              type="password"
              placeholder="••••••••"
              className="bg-zinc-800 border-zinc-700 text-white mt-1 max-w-md"
            />
          </div>
          <div>
            <Label className="text-zinc-300">New Password</Label>
            <Input
              type="password"
              placeholder="••••••••"
              className="bg-zinc-800 border-zinc-700 text-white mt-1 max-w-md"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Confirm New Password</Label>
            <Input
              type="password"
              placeholder="••••••••"
              className="bg-zinc-800 border-zinc-700 text-white mt-1 max-w-md"
            />
          </div>
          <div className="flex justify-end max-w-md">
            <Button variant="outline" className="border-zinc-700">
              Update Password
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Globe className="w-5 h-5 text-blue-500" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-zinc-500">Account ID</p>
              <p className="text-white font-mono">{user?.id?.slice(0, 8)}...</p>
            </div>
            <div>
              <p className="text-zinc-500">Member Since</p>
              <p className="text-white">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</p>
            </div>
            <div>
              <p className="text-zinc-500">Account Type</p>
              <p className="text-white capitalize">{user?.role}</p>
            </div>
            <div>
              <p className="text-zinc-500">Status</p>
              <Badge variant="outline" className="status-online mt-1">Active</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
